/**
 * Implement a class that represents an OpenAI client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import { nanoid } from "nanoid";
import OpenAI from "openai";
import { zodToJsonSchema } from "zod-to-json-schema";
import { Message, ToolCall } from "../message";
import { Thread } from "../thread";
import { Tool } from "../tool";
import { traceable } from "../tracing";
import { BaseModelDriver } from "./base";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;
const JSON_SCHEMA_URL = "http://json-schema.org/draft-07/schema#";

/**
 * Available OpenAI models
 */
export type OpenAIModel =
  | "gpt-4o"
  | "gpt-4o-mini"
  | "gpt-4"
  | "gpt-4-turbo"
  | "gpt-3.5-turbo"
  | "gpt-4-vision-preview";

/**
 * Configuration options for the OpenAI driver
 */
export interface OpenAIConfig {
  /**
   * OpenAI API Key
   */
  apiKey?: string;

  /**
   * Model to use
   * @default "gpt-4o"
   */
  model?: OpenAIModel;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Whether to enable tracing
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;
}

/**
 * Represents an OpenAI client and provides tools for converting messages to/from the LLM
 */
export class OpenAIThreadDriver extends BaseModelDriver {
  /**
   * OpenAI API Client
   */
  private client: OpenAI;

  /**
   * OpenAI model to use
   */
  private model: OpenAIModel | string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;

  /**
   * Returns a list of available OpenAI models
   */
  static getAvailableModels(): OpenAIModel[] {
    return [
      "gpt-4o",
      "gpt-4o-mini",
      "gpt-4",
      "gpt-4-turbo",
      "gpt-3.5-turbo",
      "gpt-4-vision-preview"
    ];
  }

  /**
   * Create a new OpenAI driver
   * @param config Configuration options
   */
  constructor(config: OpenAIConfig = {}) {
    super();

    // Get API Key from config or environment variable
    const apiKey = config.apiKey || process.env.OPENAI_API_KEY;
    if (!apiKey) {
      throw new Error(
        "OpenAI API Key must be provided in config or as OPENAI_API_KEY environment variable"
      );
    }

    // Create a base client
    const baseClient = new OpenAI({
      apiKey,
    });
    
    // Only wrap with tracing if trace is enabled (default to false)
    if (config.trace ?? false) {
      // Create a proxy around the OpenAI client to intercept API calls
      this.client = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          
          // Handle nested properties (chat.completions.create)
          if (prop === 'chat' && value && typeof value === 'object') {
            return new Proxy(value, {
              get: (chatTarget, chatProp, chatReceiver) => {
                const chatValue = Reflect.get(chatTarget, chatProp, chatReceiver);
                
                // Handle completions property
                if (chatProp === 'completions' && chatValue && typeof chatValue === 'object') {
                  return new Proxy(chatValue, {
                    get: (completionsTarget, completionsProp, completionsReceiver) => {
                      const completionsValue = Reflect.get(completionsTarget, completionsProp, completionsReceiver);
                      
                      // Wrap the create method with tracing
                      if (completionsProp === 'create' && typeof completionsValue === 'function') {
                        return traceable(completionsValue.bind(completionsTarget), {
                          run_type: "llm",
                          name: "openai",
                          ...config.traceMetadata,
                          aggregator: (args: any[], result: any) => {
                            // Extract relevant data for tracing
                            const params = args[0] || {};
                            
                            // For streaming responses, we can't easily get metrics, so just return the input
                            if (params.stream) {
                              return {
                                model: params.model,
                                input: {
                                  messages: params.messages,
                                  temperature: params.temperature,
                                  max_tokens: params.max_tokens,
                                  tools: params.tools ? params.tools.length : 0,
                                },
                                streaming: true
                              };
                            }
                            
                            // For non-streaming, include response data
                            return {
                              model: params.model,
                              input: {
                                messages: params.messages,
                                temperature: params.temperature,
                                max_tokens: params.max_tokens,
                                tools: params.tools ? params.tools.length : 0,
                              },
                              output: result.choices?.[0]?.message,
                              metrics: result.usage ? {
                                prompt_tokens: result.usage.prompt_tokens,
                                completion_tokens: result.usage.completion_tokens,
                                total_tokens: result.usage.total_tokens,
                              } : undefined,
                              finish_reason: result.choices?.[0]?.finish_reason
                            };
                          }
                        });
                      }
                      
                      return completionsValue;
                    }
                  });
                }
                
                return chatValue;
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.client = baseClient;
    }
    
    // Set configuration options
    this.model = config.model || "gpt-4o";
    this.temperature = config.temperature ?? 0.7;
    this.maxTokens = config.maxTokens;
    this.cache = config.cache ?? false;
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    this.debug("Sending message to OpenAI API", { model: this.model });

    // Format messages for OpenAI API
    const messages = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: OpenAI.Chat.ChatCompletionCreateParams = {
        model: this.model,
        messages,
        temperature: this.temperature,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        params.tool_choice = "auto";
      }

      // Make the API call
      const response = await this.client.chat.completions.create(params);

      // Get the last choice from the response
      const lastChoice = response.choices[0];
      if (!lastChoice) {
        throw new Error("No response from OpenAI API");
      }

      const choice = lastChoice.message;
      if (!choice) {
        throw new Error("Empty response from OpenAI API");
      }

      // Create a new message from the response
      const message = new Message({
        role: "assistant",
        content: choice.content || "",
        toolCalls: this.parseToolCalls(choice.tool_calls),
        cache: this.cache,
      });

      // Add the message to the thread
      thread.push(message);

      // Log usage metrics if available
      if (response.usage) {
        this.debug("OpenAI API usage metrics", {
          inputTokens: response.usage.prompt_tokens,
          outputTokens: response.usage.completion_tokens,
          totalTokens: response.usage.total_tokens,
        });
      }

      return thread;
    } catch (error) {
      this.error("Error sending message to OpenAI API", { error });
      throw error;
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    this.debug("Streaming message to OpenAI API", { model: this.model });

    // Format messages for OpenAI API
    const messages = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: OpenAI.Chat.ChatCompletionCreateParams = {
        model: this.model,
        messages,
        temperature: this.temperature,
        stream: true,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        params.tool_choice = "auto";
      }

      // Create a new message to add to the thread
      const message = new Message({
        role: "assistant",
        content: "",
        cache: this.cache,
      });

      // Make the streaming API call
      const stream = await this.client.chat.completions.create(params);

      // Create a streaming content generator
      const streamContent = this.createStreamGenerator(stream, message);

      // Yield the stream
      yield { stream: streamContent, message };

      // Add the message to the thread
      thread.push(message);

      // Return the updated thread
      return thread;
    } catch (error) {
      this.error("Error streaming message to OpenAI API", { error });
      throw error;
    }
  }

  /**
   * Create a stream generator that handles updates to the message
   */
  private async *createStreamGenerator(
    stream: AsyncIterable<OpenAI.Chat.ChatCompletionChunk>,
    message: Message
  ): AsyncGenerator<string, void> {
    let toolCallsStarted = false;
    let currentToolCalls: any[] = [];

    try {
      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta;

        if (!delta) continue;

        // Handle content updates
        if (delta.content) {
          // Create a new message with updated content
          const updatedMessage = new Message({
            role: "assistant",
            content: (message.content || "") + delta.content,
            toolCalls: message.toolCalls,
            cache: this.cache,
          });
          
          // Copy the updated message properties
          Object.assign(message, updatedMessage);
          
          yield delta.content;
        }

        // Handle tool call updates
        if (delta.tool_calls) {
          if (!toolCallsStarted) {
            // First tool call chunk
            toolCallsStarted = true;
            currentToolCalls = delta.tool_calls.map(call => ({ ...call }));
          } else {
            // Subsequent tool call chunks - need to merge them
            for (const toolCall of delta.tool_calls) {
              const existingCall = currentToolCalls.find(c => c.index === toolCall.index);
              
              if (existingCall) {
                // Update function content if provided
                if (toolCall.function) {
                  if (!existingCall.function) {
                    existingCall.function = {};
                  }
                  
                  if (toolCall.function.name && !existingCall.function.name) {
                    existingCall.function.name = toolCall.function.name;
                  }
                  
                  if (toolCall.function.arguments) {
                    existingCall.function.arguments = (existingCall.function.arguments || "") + 
                      toolCall.function.arguments;
                  }
                }
              } else {
                // New tool call
                currentToolCalls.push({ ...toolCall });
              }
            }
          }
        }
      }

      // Process the tool calls after the stream completes
      if (currentToolCalls.length > 0) {
        const parsedToolCalls = this.parseToolCalls(currentToolCalls);
        
        // Create a new message with the tool calls
        const updatedMessage = new Message({
          role: "assistant",
          content: message.content || "",
          toolCalls: parsedToolCalls,
          cache: this.cache,
        });
        
        // Copy the updated message properties
        Object.assign(message, updatedMessage);
      }
    } catch (error) {
      this.error("Error processing OpenAI stream", { error });
      throw error;
    }
  }

  /**
   * Format messages from the Thread object to the OpenAI API format
   */
  private formatMessagesForAPI(thread: Thread): OpenAI.Chat.ChatCompletionMessageParam[] {
    const messages: OpenAI.Chat.ChatCompletionMessageParam[] = [];

    for (const message of thread.all()) {
      // Process attachments if they exist
      if (message.attachments && message.attachments.length > 0) {
        const contentParts: (OpenAI.Chat.ChatCompletionContentPart)[] = [];
        
        // Add text content if it exists
        if (message.content) {
          contentParts.push({
            type: "text",
            text: message.content,
          });
        }
        
        // Add each attachment
        for (const attachment of message.attachments) {
          if (attachment.type === "image" && attachment.source.uri) {
            contentParts.push({
              type: "image_url",
              image_url: {
                url: attachment.source.uri,
                detail: "auto",
              },
            });
          }
        }
        
        // OpenAI has specific typing requirements for different roles
        if (message.role === "user") {
          messages.push({
            role: "user",
            content: contentParts as any, // Type cast to bypass strict typing
          });
        } else if (message.role === "assistant") {
          messages.push({
            role: "assistant",
            content: contentParts as any, // Type cast to bypass strict typing
          });
        } else if (message.role === "system") {
          messages.push({
            role: "system",
            content: contentParts as any, // Type cast to bypass strict typing
          });
        }
        continue;
      }

      // Handle tool results (these are created from user messages with toolResults)
      if (message.toolResults && message.toolResults.length > 0) {
        // First add the user message if it has content
        if (message.content) {
          messages.push({
            role: "user",
            content: message.content,
          });
        }

        // Then add each tool result as a separate message
        for (const toolResult of message.toolResults) {
          messages.push({
            role: "tool",
            tool_call_id: toolResult.toolUseId,
            content: JSON.stringify(toolResult.result),
          });
        }
        continue;
      }

      // Handle tool calls in assistant messages
      if (message.role === "assistant" && message.toolCalls && message.toolCalls.length > 0) {
        const toolCalls = message.toolCalls.map(call => ({
          id: call.toolUseId,
          type: "function" as const,
          function: {
            name: call.name,
            arguments: JSON.stringify(call.arguments),
          },
        }));

        messages.push({
          role: "assistant",
          content: message.content || null,
          tool_calls: toolCalls,
        });
        continue;
      }

      // Default case for simple messages
      if (message.role === "user") {
        messages.push({
          role: "user",
          content: message.content || "",
        });
      } else if (message.role === "assistant") {
        messages.push({
          role: "assistant",
          content: message.content || "",
        });
      } else if (message.role === "system") {
        messages.push({
          role: "system",
          content: message.content || "",
        });
      }
    }

    return messages;
  }

  /**
   * Create tool definitions for the OpenAI API
   */
  private createToolDefinitions(tools: Tool[]): OpenAI.Chat.ChatCompletionTool[] {
    return tools.map(tool => ({
      type: "function",
      function: {
        name: tool.getName(),
        description: tool.getDescription(),
        parameters: {
          ...zodToJsonSchema(tool.getParameters()),
          $schema: JSON_SCHEMA_URL,
        },
      },
    }));
  }

  /**
   * Parse tool calls from the OpenAI API response
   */
  private parseToolCalls(toolCalls?: OpenAI.Chat.ChatCompletionMessageToolCall[]): ToolCall[] | undefined {
    if (!toolCalls || toolCalls.length === 0) {
      return undefined;
    }

    return toolCalls.map(toolCall => {
      if (toolCall.type === 'function' && toolCall.function.name) {
        try {
          const args = JSON.parse(toolCall.function.arguments || "{}");
          return {
            toolUseId: toolCall.id,
            name: toolCall.function.name,
            arguments: args,
          };
        } catch (error) {
          this.error("Error parsing tool call arguments", {
            error,
            toolCall,
          });
          return {
            toolUseId: toolCall.id,
            name: toolCall.function.name,
            arguments: {},
          };
        }
      }
      
      this.error("Unknown tool call type", { toolCall });
      return {
        toolUseId: toolCall.id || nanoid(),
        name: toolCall.function?.name || "unknown",
        arguments: {},
      };
    });
  }
} 